// Model Gateway validation utilities

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate model name
 * Name is required and must not be empty after trimming
 *
 * @param name - The model name to validate
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateName = (name: string): ValidationResult => {
  const trimmedName = name.trim();

  if (trimmedName.length === 0) {
    return {
      isValid: false,
      error: 'Name is required',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate model slug
 * Slug must contain only alphanumeric characters, dots, underscores, and hyphens
 * Pattern: ^[a-zA-Z0-9._-]+$
 *
 * @param slug - The model slug to validate
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateSlug = (slug: string): ValidationResult => {
  const trimmedSlug = slug.trim();

  if (trimmedSlug.length === 0) {
    return {
      isValid: false,
      error: 'Slug is required',
    };
  }

  const slugPattern = /^[a-zA-Z0-9._-]+$/;

  if (!slugPattern.test(trimmedSlug)) {
    return {
      isValid: false,
      error: 'Slug can only contain alphanumeric characters, dots, underscores, and hyphens',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate routing target weight
 * Weight must be a number greater than or equal to 1
 *
 * @param weight - The weight value to validate
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateWeight = (weight: number): ValidationResult => {
  if (typeof weight !== 'number' || isNaN(weight)) {
    return {
      isValid: false,
      error: 'Weight must be a valid number',
    };
  }

  if (weight < 1) {
    return {
      isValid: false,
      error: 'Weight must be at least 1',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate required fields in a form
 * Checks if all specified fields have non-empty values
 *
 * @param fields - Object containing field names and their values
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateRequiredFields = (
  fields: Record<string, string | number | null | undefined>
): ValidationResult => {
  const emptyFields: string[] = [];

  for (const [fieldName, value] of Object.entries(fields)) {
    if (value === null || value === undefined) {
      emptyFields.push(fieldName);
      continue;
    }

    if (typeof value === 'string' && value.trim().length === 0) {
      emptyFields.push(fieldName);
      continue;
    }
  }

  if (emptyFields.length > 0) {
    const fieldList = emptyFields.join(', ');
    return {
      isValid: false,
      error: `Required fields are missing: ${fieldList}`,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate URL for Custom Model (BYOM) base URLs
 * URL must be a valid HTTPS URL
 *
 * @param url - The URL to validate
 * @returns ValidationResult with isValid flag and optional error message
 */
export const validateUrl = (url: string): ValidationResult => {
  const trimmedUrl = url.trim();

  if (trimmedUrl.length === 0) {
    return {
      isValid: false,
      error: 'URL is required',
    };
  }

  try {
    const parsedUrl = new URL(trimmedUrl);

    // Ensure HTTPS protocol for security
    if (parsedUrl.protocol !== 'https:') {
      return {
        isValid: false,
        error: 'URL must use HTTPS protocol',
      };
    }

    return {
      isValid: true,
    };
  } catch {
    return {
      isValid: false,
      error: 'Invalid URL format',
    };
  }
};

/**
 * Validation patterns for Model Gateway
 */
export const MODEL_GATEWAY_PATTERNS = {
  SLUG: /^[a-zA-Z0-9._-]+$/,
  HTTPS_URL: /^https:\/\/.+$/,
} as const;
